import React, { useMemo } from 'react';
import { Flex, Spinner } from '@patternfly/react-core';
import { useTranslation } from '../../../../localization/useTranslation';
import ModelType from '../../../Model/ModelType';
import './DialPanel.scss';
import T from '../../../../localization/LocalizationKeys';
import Icons, { Size } from '../../../Common/Icons';
import DialGraphComponent from './DialGraphComponent';
import _ from 'lodash';
import useInterval from '../../../../utils/hooks/useInterval';
import dataSources from '../../datasources';
import initialOptions from './intialOptions';
import fixedOptions from './fixedOptions';
import useMeasurementSystem from 'components/Value/useMeasurementSystem';

const DEFAULT_REFRESH_INTERVAL = 5; // 5 minutes
const MIN_REFRESH_INTERVAL = 5; // 5 minutes
const DEFAULT_DECIMALS = 2;
const DEFAULT_FORMATTER = `<div style="text-align:center">
<span style="font-size:12px">{y}</span><br/>
<span style="font-size:12px;opacity:0.4">{{unit}}</span><br/>
<span style="font-size:12px;opacity:0.4">at {{timestamp}}</span>
</div>
`;

export const SELECTABLE_DATA_SOURCES = Object.freeze(_.map(_.pick(dataSources, ['realTimeTelemetry', 'lastValue']), ({ label }, type) => ({ type, label })));
// Here we select the default value for dataSource property
// TODO: use const for lastValue, or maybe use a default: true in the list of selectable data source.
export const DEFAULT_DATA_SOURCE = _.find(SELECTABLE_DATA_SOURCES, { type: 'lastValue' });

const DialPanelV2 = ({ data, api }) => {

  const { refreshInterval } = data;
  const { hasError, isLoading, signalData } = data.signalValues;
  const plotlines = data.plotlineValues?.plotlines ?? [];
  useInterval(api.reload, Math.max(MIN_REFRESH_INTERVAL, (refreshInterval ?? DEFAULT_REFRESH_INTERVAL) * 60000));

  const T = useTranslation();
  const loadingDescription = hasError ? T.common.error.generic : T.common.loading;
  const { convertToUserValue } = useMeasurementSystem({ });
  const options = useMemo(() => {
    const newOptions = _.cloneDeep(data.options ?? initialOptions);
    const max = data.signalValues.signals?.[0] ? convertToUserValue({ value: newOptions.yAxis.max, schemaUnit: data.signalValues.signals[0] }) : newOptions.yAxis.max;
    const min = data.signalValues.signals?.[0] ? convertToUserValue({ value: newOptions.yAxis.min, schemaUnit: data.signalValues.signals[0] }) : newOptions.yAxis.min;
    newOptions.yAxis.max = max;
    newOptions.yAxis.min = min;
    return _.merge({}, newOptions, fixedOptions, { time: { timezoneOffset: new Date().getTimezoneOffset() } });
  }, [convertToUserValue, data.options, data.signalValues.signals]);

  const renderTelemetry = () => {
    if (_.isEmpty(signalData)) {
      return (<>No Data</>);
    }
    if (!_.isEmpty(signalData.dataPoints)) {
      return (<>No Data points</>);
    }
    const formatData = (values, decimals) => {
      return _.map(values, (value) => {
        const v = _.isNumber(value?.[1]) ? Number(value?.[1].toFixed(decimals)) : value?.[1];
        return [value?.[0], v];
      });

    };
    if (signalData) {
      const formattedSignalData = _.map(signalData, (signal) => {
        return {
          ...signal,
          dataPoints: formatData(signal.dataPoints, data?.decimals || DEFAULT_DECIMALS),
          displayName: signal.signalDisplayName
        };
      });
      if (!_.isEmpty(formattedSignalData)) {
        return (
          <DialGraphComponent
            signal={_.first(formattedSignalData)}
            options={options}
            valueFormatter={data?.valueFormatter || DEFAULT_FORMATTER}
            reflowTrigger={api.size.width}
            plotlines={plotlines}
          />
        );
      }
    }
  };

  return (
    <div className={'dial-panel-container'}>
      { (isLoading || hasError) && <Flex alignItems={{ default: 'alignItemsCenter' }}>
        <Spinner size="md" />
        <span>{ loadingDescription }</span>
      </Flex> }
      { renderTelemetry() }
    </div>
  );
};

DialPanelV2.models = [
  {
    path: 'refreshInterval',
    type: ModelType.NUMBER,
    label: T.common.refreshInterval,
    placeholder: _.toString(DEFAULT_REFRESH_INTERVAL)
  },
  {
    path: 'dataSource',
    label: T.common.dataSource,
    type: ModelType.OPTIONSV2,
    options: _.map(SELECTABLE_DATA_SOURCES, ({ type, label }) => ({ text: label, value: type })),
    placeholder: DEFAULT_DATA_SOURCE.label
  },
  {
    type: ModelType.JSON_EDIT_DIALOG,
    path: 'options',
    label: T.dashboard.editor.inputs.json.options,
    properties: {
      height: '380px',
      width: '90%',
      initialValue: initialOptions
    }
  },
  {
    type: ModelType.NUMBER,
    path: 'decimals',
    label: 'Decimals',
    placeholder: DEFAULT_DECIMALS
  },
  {
    type: ModelType.TEXTAREA,
    path: 'valueFormatter',
    label: 'Value Format',
    properties: {
      initialValue: DEFAULT_FORMATTER
    }
  }
];


DialPanelV2.info = {
  label: T.dashboard.panels.types.dial,
  icon: <Icons.Dial size={Size.LARGE}/>,
  help: 'panels/dial'
};

DialPanelV2.properties = panelProperties => {
  const modifiedProps = {
    signalValues: {
      type: panelProperties?.dataSource ?? DEFAULT_DATA_SOURCE.type
    },
    plotlineValues: {
      type: 'plotlines'
    }
  };
  return modifiedProps;
};


export default React.memo(DialPanelV2);
